import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import CustomTextInput from "../../components/ui/CustomTextInput/CustomTextInput";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import { requestPasswordResetValidators } from "../../lib/validations/authValidations";
import { toastService } from "../../providers";
import { useRequestReactivation } from "../../hooks/queries/useAuthQueries";
import AuthLayout from "../../components/auth/authLayout/AuthLayout";
import AuthCard from "../../components/auth/authCard/AuthCard";
import CustomNotificationBanner from "../../components/ui/CustomNotificationBanner/CustomNotificationBanner";
import { useLocationBanner } from "./hooks/useLocationBanner";

function RequestReactivation() {
  const navigate = useNavigate();
  const { banner, dismissBanner } = useLocationBanner();
  const { variant, title, message } = banner ?? {};
  const { mutate: requestReactivation, isPending } = useRequestReactivation();

  const { control, handleSubmit } = useForm({
    defaultValues: { email: "" },
    mode: "onTouched",
  });

  const isAlreadyActivated = (response) =>
    response?.remarks?.toLowerCase().includes("already activated");

  const onSubmit = (values) => {
    requestReactivation(values.email, {
      onSuccess: (response) => {
        if (isAlreadyActivated(response)) {
          navigate("/auth/login", {
            replace: true,
            state: { bannerReason: "already_used" },
          });
          return;
        }
        toastService.success("Activation link sent to your email");
        navigate("/auth/login");
      },
      onError: (_error) => {
        toastService.error("Failed to send activation link");
      },
    });
  };

  return (
    <AuthLayout>
      <AuthCard
        title="Activate Your Account"
        subtitle={
          <div className="text-black-50 text-xs sm:text-sm">
            Enter your email to receive an activation link
          </div>
        }
      >
        {banner && (
          <CustomNotificationBanner
            variant={variant}
            title={title}
            message={message}
            mb="md"
            onClose={dismissBanner}
          />
        )}
        <form
          className="flex flex-col gap-2 md:gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="email"
            control={control}
            rules={{
              validate: (v) => requestPasswordResetValidators.email(v) ?? true,
            }}
            render={({ field, fieldState }) => (
              <CustomTextInput
                label="Email:"
                radius="md"
                type="email"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={isPending}
                styles={{
                  input: {
                    fontWeight: "600",
                    fontSize: "14px",
                  },
                }}
              />
            )}
          />
          <CustomButton
            type="submit"
            radius="xl"
            size="sm"
            fullWidth
            disabled={isPending}
            loading={isPending}
          >
            Send Reactivation Link
          </CustomButton>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default RequestReactivation;
